import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { Box } from "@mui/material";

import SideMenu from "./SideMenu";
import NavBar from "./NavBar";
import AuthLayout from "./AuthLayout";
import MainSection from "./MainSection";
import { ROUTE_PATHS } from "../../utils/RoutesPaths";
import { isClientCompanySelectionPath } from "../../utils/authPortal";
import {
  isUnderClientResetPasswordPath,
} from "../../utils/clientPortal";
import {
  isCompanySignupOnboardingPath,
  isUnderCompanyResetPasswordPath,
  mustCompleteCompanyRegistration,
} from "../../utils/companyRegistration";
import {
  isAuthCenteredFormPath,
  isAuthWideCenteredFormPath,
} from "../../utils/authLayoutPaths";
import CompanyPendingApprovalLogoutButton from "../../containers/Auth/CompanyPendingApprovalLogoutButton";
import "./Layout.scss";
import "./AuthLayout/auth-layout-buttons.scss";

const drawerWidth = 216;
const collapsedDrawerWidth = 72;
const navbarHeight = 64;

const Layout = ({ isAuth, children }) => {
  const [sideMenuOpen, setSideMenuOpen] = useState(true);
  const [mobileSideMenuOpen, setMobileSideMenuOpen] = useState(false);

  const location = useLocation();

  useEffect(() => {
    const isViewEventRoute = /^\/admin\/events\/(\d+)\/view$/.test(
      location.pathname,
    );
    const storedViewEventTab = sessionStorage.getItem("viewEventTab");
    if (storedViewEventTab && !isViewEventRoute) {
      sessionStorage.removeItem("viewEventTab");
    }
  }, [location]);

  const detectSideMenuOpen = (isOpen) => {
    setSideMenuOpen(isOpen);
  };
  const detectMobileSideMenuOpen = (isOpen) => {
    setMobileSideMenuOpen(isOpen);
  };

  // Checking for changePassword as it's an authenticated page but needs the usual auth pages layout
  const useMainShell =
    isAuth &&
    location.pathname !== ROUTE_PATHS.changePassword &&
    !isClientCompanySelectionPath(location.pathname) &&
    !isUnderClientResetPasswordPath(location.pathname) &&
    !isUnderCompanyResetPasswordPath(location.pathname) &&
    !mustCompleteCompanyRegistration();

  if (location.pathname === "/ui-kit") {
    return children;
  }

  return useMainShell ? (
    <Box
      sx={{
        display: "flex",
        backgroundColor: "var(--color-primary-0)",
        "--layout-sidebar-offset": {
          xs: "0px",
          lg: `${sideMenuOpen ? drawerWidth : collapsedDrawerWidth}px`,
        },
      }}
    >
      <NavBar
        sideMenuOpen={sideMenuOpen}
        drawerWidth={drawerWidth}
        collapsedDrawerWidth={collapsedDrawerWidth}
        openMobileSideMenu={detectMobileSideMenuOpen}
      />
      <SideMenu
        detectSideMenuOpen={detectSideMenuOpen}
        closeMobileSideMenu={() => detectMobileSideMenuOpen(false)}
        drawerWidth={drawerWidth}
        mobileSideMenuOpen={mobileSideMenuOpen}
      />
      <MainSection
        sideMenuOpen={sideMenuOpen}
        drawerWidth={drawerWidth}
        collapsedDrawerWidth={collapsedDrawerWidth}
        navbarHeight={navbarHeight}>
        {children}
      </MainSection>
    </Box>
  ) : (
    <AuthLayout
      content={children}
      centerForm={isAuthCenteredFormPath(location.pathname)}
      centerFormWide={isAuthWideCenteredFormPath(location.pathname)}
      variant={
        isCompanySignupOnboardingPath(location.pathname)
          ? "onboarding"
          : undefined
      }
      headerEndExtra={
        location.pathname === ROUTE_PATHS.companyPendingApproval ? (
          <CompanyPendingApprovalLogoutButton />
        ) : null
      }
    />
  );
};

Layout.propTypes = {
  isAuth: PropTypes.bool,
  children: PropTypes.node,
};

export default Layout;
